/* eslint-disable */
import type { Color } from "../cash/ui/Color.pb";
import type { SharingContent } from "./api.pb";
import type { ScenarioPlan } from "./scenarios.pb";

export enum AppMessageTheme {
  DEFAULT = "DEFAULT",
  DARK = "DARK",
  /**
   * CUSTOM - A custom theme
   * Theme colors should be specified with `AppMessageThemeColors`
   */
  CUSTOM = "CUSTOM",
}

export enum PopupMessagePlacement {
  BALANCE_TAB = "BALANCE_TAB",
  CARD_TAB = "CARD_TAB",
  PAYMENT_PAD_TAB = "PAYMENT_PAD_TAB",
  INVESTING_TAB = "INVESTING_TAB",
  ACTIVITY_TAB = "ACTIVITY_TAB",
}

/**
 * The app message data contains the payload that is sent to the apps, as well as data used to
 * construct the push notification.
 */
export interface AppMessageData {
  /** The payload that is sent to app when it requests the app message for the message token. */
  payload?: AppMessagePayload;
  /** The notification to be posted to the user when the message is sent to the app. */
  notification?: AppMessageNotification;
}

/**
 * The payload that is sent to the apps. At most one of the templates will be defined. If no
 * templates are defined, the app deletes a previous app message for the message token.
 */
export interface AppMessagePayload {
  /** The unique token of this message. */
  message_token?: string;
  /**
   * The priority of this message on the app home screen which is shared with reward status.
   * Higher priority items get displayed before lower priority items.
   */
  priority?: number;
  /** The text to show in the home screen button for this message. When null, default text is used. */
  home_button_text?: string;
  /**
   * When true, the app auto-opens this message's template the next time the user lands on the
   * home screen.
   * Deprecated: Use presentation_mode instead.
   *
   * @deprecated
   */
  force_open?: boolean;
  presentation_mode?: AppMessagePayloadPresentationMode;
  /** The theme to apply to this app message. For 2.15, only applies to AppMessagePromoTemplate. */
  theme?: AppMessageTheme;
  /** An exhaustive list of foreground/background colors to use for custom theming. */
  theme_colors?: AppMessageThemeColors;
  /** The message has a header and bottom-aligned image. */
  whats_new?: AppMessageWhatsNewTemplate;
  /** The message has banner at the top, and a header underneath. */
  banner?: AppMessageBannerTemplate;
  /** The message has a header and an edge-to-edge image. */
  promo?: AppMessagePromoTemplate;
  /** The message has a list of headlines, one for each feature. */
  feature_list?: AppMessageFeatureListTemplate;
  /** The message has an [X] button. Action buttons are stacked rather than navigation-style. */
  button_stack?: AppMessageButtonStackTemplate;
  /**
   * The message has a header and two buttons and gets displayed in the home screen drawer.
   * Added for version 2.10.4.
   */
  drawer?: AppMessageDrawerTemplate;
  /**
   * The message has a toggle and a single button at the bottom.
   * Added for version 2.10.4.
   */
  toggle?: AppMessageToggleTemplate;
  /** The message content and layout are defined with HTML. */
  html?: AppMessageHtmlTemplate;
  /**
   * Clicking the push notification or home screen button directly invokes an action without
   * showing an message screen.
   */
  direct?: AppMessageDirectActionTemplate;
  /** The message appears at the top of the customer's activity screen. */
  foreground_video?: AppMessageForegroundVideoTemplate;
  /**
   * The message is used as an in-app promotion, could be displayed in settings, activity, or
   * status results.
   */
  in_app_promo?: AppMessageInAppPromoTemplate;
  /**
   * The number of new badges to be displayed on the app main view, will be cleared
   * when customer navigates to the activity feed.
   * DEPRECATED: replaced with should_badge_activity field
   *
   * @deprecated
   */
  increment_activity_badge_count_by?: number;
  /**
   * Whether the given app message should badge the activity tab screen.
   * This exists for activity screen app messages.
   */
  should_badge_activity?: boolean;
  /**
   * Whether the given app message should have an in app notif.
   * Do not use with AppMessageNotification.
   * Copied over from InAppNotificationMessage in bulletin. Changes must be made in both repos.
   */
  in_app_notification_message?: InAppNotificationMessage;
}

/** Specifies how the app should handle opening the message. */
export enum AppMessagePayloadPresentationMode {
  /**
   * HOME_BUTTON - When this is used, home_button_text should also be set.
   *
   * @deprecated
   */
  HOME_BUTTON = "HOME_BUTTON",
  /** ACTIVITY_INLINE - Opens this message's template as a promotion in the Activity view. */
  ACTIVITY_INLINE = "ACTIVITY_INLINE",
  /**
   * PROFILE_PERSONAL_INLINE - Opens this message's template as a promo in the Personal section of the customer's profile.
   * AppMessageInAppPromoTemplate is the only supported template for this presentation mode.
   */
  PROFILE_PERSONAL_INLINE = "PROFILE_PERSONAL_INLINE",
  /**
   * HOME_SCREEN - Auto-opens this message's template the next time the user lands on the home screen.
   * Note that this equates to how force_open was previously used, so the message may be
   * shown regardless of the current screen if the customer has tapped on a notification.
   */
  HOME_SCREEN = "HOME_SCREEN",
  /**
   * PROFILE_CASH_CARD - Opens this message's template the next time the customer navigates to the cash card in their
   * profile.
   *
   * @deprecated
   */
  PROFILE_CASH_CARD = "PROFILE_CASH_CARD",
}

export interface AppMessageWhatsNewTemplate {
  /** The text to place above the headline, e.g. “NEW”. */
  headline_badge?: string;
  /** The headline text. */
  headline?: AppMessageHeadline;
  /** The URL of the image to show at the bottom of the screen (full width). */
  bottom_image_url?: string;
  /** The primary action the user can take. */
  primary_navigation_action?: AppMessageAction;
  /** A secondary action the user can take. */
  secondary_navigation_action?: AppMessageAction;
  /** A video background URL. Currently only supported on iPhone. */
  background_video?: AppMessageBackgroundVideo;
  /**
   * The value to pass in the RegisterAppMessageActionRequest when the dismiss button is tapped.
   * If present, the clients should show an [X] in the app.
   */
  dismiss_action_identifier?: string;
  /** A background image meant to cover the entire background of the app message. */
  background_image_url?: string;
  display_mode?: AppMessageWhatsNewTemplateWhatsNewDisplayMode;
  /** The image to use in the bulletin app message. */
  bulletin_image?: Image;
  /**
   * The placement to use for the bulletin Popup Message.
   * This is a short term hack to provide a way to create a bulletin Popup Message with any of the
   * available placements. Since franklin WhatsNew messages don't support the same placements as
   * bulletin Popup Messages, sending a message for a template that has this field populated will
   * only enqueue a message in bulletin. A franklin app message will not be created.
   */
  popup_message_placement?: PopupMessagePlacement;
}

export enum AppMessageWhatsNewTemplateWhatsNewDisplayMode {
  /** FULL_SCREEN - Message is displayed as the traditional full screen takeover. */
  FULL_SCREEN = "FULL_SCREEN",
  /** DIALOG - Message is displayed as a dialog, with action buttons stacked. */
  DIALOG = "DIALOG",
}

export interface AppMessageBannerTemplate {
  /** The URL of the image to show at the top of the screen (full width). */
  banner_image_url?: string;
  /** The text to place above the headline, e.g. “NEW”. */
  headline_badge?: string;
  /** The headline text to show under the banner. */
  headline?: AppMessageHeadline;
  /** The primary action the user can take. */
  primary_navigation_action?: AppMessageAction;
  /** A secondary action the user can take. */
  secondary_navigation_action?: AppMessageAction;
}

/** A promotional app message with a bold, edge-to-edge image. */
export interface AppMessagePromoTemplate {
  /** The headline text. */
  headline?: AppMessageHeadline;
  /** The text to place in the badge below the headline, e.g. “NEW”. */
  headline_badge?: string;
  /** The URL of the image to show below the badge that stretches the full width of the screen. */
  image_url?: string;
  /**
   * The URL of a fallback image to show instead of the main image, on devices whose screen is too
   * wide to fit the main image edge-to-edge and the other elements of the template. The fallback
   * image needs to look good with background real estate on all sides.
   */
  fallback_image_url?: string;
  /** The primary action the user can take. */
  primary_navigation_action?: AppMessageAction;
  /** A secondary action the user can take. */
  secondary_navigation_action?: AppMessageAction;
  /**
   * The value to pass in the RegisterAppMessageActionRequest when the dismiss button is tapped.
   * If present, the clients should show an [X] in the app.
   */
  dismiss_action_identifier?: string;
}

export interface AppMessageFeatureListTemplate {
  /** The headlines for each feature. */
  features?: AppMessageFeature[];
  /** The primary action the user can take. */
  primary_navigation_action?: AppMessageAction;
  /** A secondary action the user can take. */
  secondary_navigation_action?: AppMessageAction;
}

export interface AppMessageButtonStackTemplate {
  /** The URL of an image/icon. Client controls sizing. */
  image_url?: string;
  /** The headline appearing above the action button(s). */
  headline?: AppMessageHeadline;
  /** A code or other snippet of text to show above the actions. */
  code_text?: string;
  /** The actions buttons (if any) this screen has. */
  actions?: AppMessageAction[];
  /** The value to pass in the RegisterAppMessageActionRequest when the dismiss button is tapped. */
  dismiss_action_identifier?: string;
}

export interface AppMessageDrawerTemplate {
  /** The title and text of the message. */
  headline?: AppMessageHeadline;
  /** The primary action the user can take. */
  primary_navigation_action?: AppMessageAction;
  /** A secondary action the user can take. */
  secondary_navigation_action?: AppMessageAction;
}

export interface AppMessageToggleTemplate {
  /** The URL of the image to show at the top of the screen. Client controls sizing. */
  image_url?: string;
  /** The title and text of the message. */
  headline?: AppMessageHeadline;
  /** The toggle. */
  toggle?: AppMessageToggle;
  /**
   * The action taken when the user clicks the button with the toggle on. The title of this
   * action is used for the button when the toggle is on.
   */
  toggle_on_navigation_action?: AppMessageAction;
  /**
   * The action taken when the user clicks the button with the toggle off. The title of this
   * action is used for the button when the toggle is off.
   */
  toggle_off_navigation_action?: AppMessageAction;
}

export interface AppMessageHtmlTemplate {
  /**
   * The HTML for the content, which may reference external image resources that clients must load.
   * Any http[s] link should be opened in an external browser, rather than in place.
   */
  html_string?: string;
  /** The primary action the user can take. */
  primary_navigation_action?: AppMessageAction;
  /** A secondary action the user can take. */
  secondary_navigation_action?: AppMessageAction;
}

export interface AppMessageDirectActionTemplate {
  /** The action that is taken as soon as the user clicks the notification or home screen button. */
  action?: AppMessageAction;
}

export interface AppMessageForegroundVideoTemplate {
  /** The main text to display. */
  headline?: AppMessageHeadline;
  /** The URL for the video to show. Video should take the width of the screen. */
  video_url?: string;
  /** The height and width of the video. */
  video_height?: number;
  video_width?: number;
  /** The URL of the image to show while we wait for the video to load. */
  placeholder_image_url?: string;
  /**
   * The URL of the image to show if the template doesn't include a video or the app can't support
   * playing the video.
   */
  fallback_image_url?: string;
  /** The percentage inset (0-100) to allow the video overlapping text/margins. */
  inset_percentage_above?: number;
  inset_percentage_below?: number;
  /** The primary action the user can take. */
  primary_navigation_action?: AppMessageAction;
  /** A secondary action the user can take. */
  secondary_navigation_action?: AppMessageAction;
  /** The image to use in the bulletin app message. */
  bulletin_image?: Image;
}

export interface AppMessageInAppPromoTemplate {
  /** The headline text. */
  headline?: AppMessageHeadline;
  /** The URL of the image to show above the text. */
  image_url?: string;
  /** The primary action the user can take. */
  primary_navigation_action?: AppMessageAction;
  /** A secondary action the user can take. */
  secondary_navigation_action?: AppMessageAction;
}

export interface AppMessageFeature {
  /** The URL of an image/icon. Client controls sizing. */
  image_url?: string;
  /** The title and text for the feature. */
  headline?: AppMessageHeadline;
}

export interface AppMessageHeadline {
  /** The main text of the headline. */
  title_text?: string;
  /** The subtext of the headline. */
  detail_text?: string;
}

export interface AppMessageToggle {
  /** The title and text of the toggle. */
  headline?: AppMessageHeadline;
  /** The default position of the toggle. */
  default_position?: boolean;
}

export interface AppMessageBackgroundVideo {
  /** The background video optimized for a 3.5 inch iPhone. */
  iphone_3_5_video_url?: string;
  /** The background video optimized for a 4 inch iPhone (iPhone 5/SE). */
  iphone_4_video_url?: string;
  /** The background video optimized for a 4.7 inch iPhone (iPhone 6/7/8). */
  iphone_4_7_video_url?: string;
  /** The background video optimized for a 5.5 inch iPhone (iPhone 6/7/8 Plus). */
  iphone_5_5_video_url?: string;
  /** The background video optimized for a 5.8 inch iPhone (iPhone X). */
  iphone_5_8_video_url?: string;
}

export interface AppMessageAction {
  /** The value to pass back in the RegisterAppMessageActionRequest. */
  action_identifier?: string;
  /** The title of the button. */
  title?: string;
  action?: AppMessageActionAction;
  /** The value used by certain Actions (see above). */
  action_argument?: string;
  /** The content for a SHARE action. */
  sharing_content?: SharingContent;
  /** The content for a SHOW_DIALOG action. */
  dialog_content?: AppMessageActionDialogContent;
  /**
   * An optional scenario plan to use for an INITIATE_CLIENT_SCENARIO action. If null, use the
   * scenario plan from the scenario plan map in the customer's profile.
   */
  scenario_plan?: ScenarioPlan;
  /**
   * Action/button-specific theming colors. If colors are not specified, the theming will fall back
   * to the theming defined by `AppMessagePayload.theme`.
   */
  theme_colors?: AppMessageActionActionThemeColors;
}

export enum AppMessageActionAction {
  /** NONE - Do nothing (beyond sending the AppMessageButtonPushedRequest). */
  NONE = "NONE",
  /** DISMISS - Dismiss the message. */
  DISMISS = "DISMISS",
  /**
   * OPEN_URL - Open a URL in the web browser.
   * action_argument is the URL.
   */
  OPEN_URL = "OPEN_URL",
  /**
   * OPEN_URL_AND_DISMISS - Open a URL in the web browser and dismiss the message.
   * action_argument is the URL.
   */
  OPEN_URL_AND_DISMISS = "OPEN_URL_AND_DISMISS",
  /**
   * DRAFT_PAYMENT - Draft a payment.
   * action_argument is the recipient's $Cashtag, email, or SMS number.
   */
  DRAFT_PAYMENT = "DRAFT_PAYMENT",
  /** SHOW_ACCOUNT_PROFILE - Show the user's account profile. */
  SHOW_ACCOUNT_PROFILE = "SHOW_ACCOUNT_PROFILE",
  /** SHOW_ACTIVITY - Show the user's activity. */
  SHOW_ACTIVITY = "SHOW_ACTIVITY",
  /**
   * SHOW_THREADED_CUSTOMER_ACTIVITY - Show the user's activity with a particular customer.
   * action_argument is the customer_token.
   */
  SHOW_THREADED_CUSTOMER_ACTIVITY = "SHOW_THREADED_CUSTOMER_ACTIVITY",
  /**
   * SHOW_PAYMENT_DETAILS - Show payment details for a particular payment.
   * action_argument is of the form "conversation_token:payment_token".
   */
  SHOW_PAYMENT_DETAILS = "SHOW_PAYMENT_DETAILS",
  /** SHOW_BUSINESS_UPSELL - Show the upgrade to business screen. */
  SHOW_BUSINESS_UPSELL = "SHOW_BUSINESS_UPSELL",
  /**
   * SHARE - Open the share screen.
   * sharing_content will contain the content to share.
   */
  SHARE = "SHARE",
  /** SHOW_INVITATION_SCREEN - Opens the invitation screen. */
  SHOW_INVITATION_SCREEN = "SHOW_INVITATION_SCREEN",
  /**
   * SET_BUSINESS_RATE_PLAN - Calls SetRatePlan (with rate_plan=BUSINESS and commit=true) after prompting the user with a
   * confirmation dialog.
   */
  SET_BUSINESS_RATE_PLAN = "SET_BUSINESS_RATE_PLAN",
  /**
   * SHOW_LINK_CARD_SCREEN - Opens the link card screen.
   * action_argument is a CashInstrumentType (DEBIT_CARD or CREDIT_CARD).
   */
  SHOW_LINK_CARD_SCREEN = "SHOW_LINK_CARD_SCREEN",
  /**
   * SHOW_DIALOG - Show a dialog.
   * dialog_content will be populated.
   * Added in 2.10.4
   */
  SHOW_DIALOG = "SHOW_DIALOG",
  /**
   * INITIATE_CLIENT_SCENARIO - Initiates a flow for a client scenario. Uses the scenario plan from the scenario_plan field
   * if it exists, otherwise gets it from the scenario plan map in the customer's profile.
   * action_argument is the client scenario to initiate.
   * e.g. ENABLE_CASH_BALANCE, DISABLE_CASH_BALANCE, ENABLE_SECURITY_LOCK, CREATE_PASSCODE, etc.
   * Added in 2.10.4
   */
  INITIATE_CLIENT_SCENARIO = "INITIATE_CLIENT_SCENARIO",
  /**
   * REGISTER_ALIAS - Initiates a flow to claim a new alias (email address or phone number).
   * action_argument is a ClientId (EMAIL or SMS).
   * Added in 2.10.4
   */
  REGISTER_ALIAS = "REGISTER_ALIAS",
  /**
   * UNREGISTER_ALIAS - Unregisters an alias (email address or phone number).
   * action_argument is of the form "client_id:alias" where client_id is either EMAIL or SMS.
   * Added in 2.10.4
   */
  UNREGISTER_ALIAS = "UNREGISTER_ALIAS",
  /**
   * INITIATE_TRANSFER_TO_STORED_BALANCE - Initiates a transfer to the customer's stored balance. Used to ask customers to refill negative balances.
   * action_argument is the amount cents to add.
   * Added in 2.11.1
   */
  INITIATE_TRANSFER_TO_STORED_BALANCE = "INITIATE_TRANSFER_TO_STORED_BALANCE",
  /**
   * SHOW_BITCOIN_DRAWER - Opens the Bitcoin drawer.
   * Added in 2.22
   */
  SHOW_BITCOIN_DRAWER = "SHOW_BITCOIN_DRAWER",
  /**
   * SHOW_CASH_DRAWER - Opens the Cash drawer.
   * Added in 2.22
   */
  SHOW_CASH_DRAWER = "SHOW_CASH_DRAWER",
  /**
   * SHOW_BOOST_PICKER_SCREEN - Opens the Boost picker screen.
   * Added in 2.41
   */
  SHOW_BOOST_PICKER_SCREEN = "SHOW_BOOST_PICKER_SCREEN",
  /**
   * START_SUPPORT_FLOW - Start support flow at the node specified by action_argument.
   * Added in 2.46
   */
  START_SUPPORT_FLOW = "START_SUPPORT_FLOW",
}

export interface AppMessageActionDialogContent {
  /** The text to display in the dialog. */
  text?: string;
  /** The primary action in the dialog. This represents the right button (or only button). */
  primary_navigation_action?: AppMessageAction;
  /** An optional secondary action in the dialog. This represents the left button. */
  secondary_navigation_action?: AppMessageAction;
}

export interface AppMessageActionActionThemeColors {
  /** Hex value representing the background color of the navigation actions, e.g. "#FFFFFF" */
  navigation_button_background_color?: string;
  /**
   * Hex value representing the background color when a navigation button is tapped.
   * If ommitted client will assume it should be darkened/lightened by a certain amount
   */
  navigation_button_highlight_background_color?: string;
  /** Hex value representing the text color of the navigation actions, e.g. "#333333" */
  navigation_button_text_color?: string;
  /**
   * Hex value representing the text color when a navigation button is tapped.
   * If ommitted client will assume it should be darkened/lightened by a certain amount
   */
  navigation_button_highlight_text_color?: string;
}

export interface AppMessageNotification {
  /** The notification message to be displayed in iOS. */
  ios_message?: string;
  /** The notification title to be displayed in the notification in Android. */
  android_title?: string;
  /** The notification body to be displayed in the notification in Android. */
  android_body?: string;
  /** The URL of the photo to use with the notification in Android. */
  photo_url?: string;
  /** When true, the iOS badge should be incremented when the notification is sent. */
  increment_badge?: boolean;
  /** The operation field of the push notification. If no operation is specified, a default is used. */
  operation?: string;
  /** The url used if operation is set to "open-url". */
  url?: string | undefined;
}

/** Used when `AppMessagePayload.theme` is `CUSTOM` */
export interface AppMessageThemeColors {
  /** Hex value representing the badge background color, e.g. "#333333" */
  badge_background_color?: string;
  /** Hex value representing the text color of a badge, e.g. "#FFFFFF" */
  badge_text_color?: string;
  /** Hex value representing the title text color, e.g. "#333333" */
  headline_title_text_color?: string;
  /** Hex value representing the detail text color, e.g. "#666666" */
  headline_detail_text_color?: string;
  /** Hex value representing the background color of the navigation actions, e.g. "#FFFFFF" */
  navigation_button_background_color?: string;
  /** Hex value representing the color of the separator between navigation buttons, e.g. "#CCCCCC" */
  navigation_button_separator_color?: string;
  /**
   * Hex value representing the background color when a navigation button is tapped.
   * If ommitted client will assume it should be darkened/lightened by a certain amount
   */
  navigation_button_highlight_background_color?: string;
  /** Hex value representing the text color of the navigation actions, e.g. "#333333" */
  navigation_button_text_color?: string;
  /**
   * Hex value representing the text color when a navigation button is tapped.
   * If ommitted client will assume it should be darkened/lightened by a certain amount
   */
  navigation_button_highlight_text_color?: string;
  /** Hex value representing the [X] in the message, e.g. "#333333" */
  dismiss_button_color?: string;
  /** Hex value representing the overall background color for the message, e.g. "#CCCCCC" */
  background_color?: string;
}

export interface InAppNotificationMessage {
  /** The number of seconds to display the message for. */
  duration?: number;
  asset_url?: string;
  action?: InAppNotificationMessageAppMessageAction;
}

export interface InAppNotificationMessageAppMessageAction {
  /** The deep link for the action. */
  url_to_open?: string;
  /** The text to display on the action button. */
  text?: string;
  /** The color of the text displayed on the button. */
  text_color?: Color;
}

export interface Image {
  image_fill?: ImageFill | undefined;
  image_inset?: ImageInset | undefined;
  image_fixed?: ImageFixed | undefined;
}

export interface ImageFill {
  asset_url?: string;
}

export interface ImageInset {
  asset_url?: string;
}

export interface ImageFixed {
  asset_url?: string;
  /** The width and height of the image, expressed in point values. */
  width?: number;
  height?: number;
}
